import { useEffect, useState } from 'react';
import type { MeasurementRow } from '@/prediction/measurements';
import { computeMetrics, type MetricsSection } from './metrics';
import { Icon } from '@/ui/Icon';

/**
 * The METRICS block of the project drawer, computed in the browser from measurements.json.
 * The title bar stays pinned and the sections scroll inside the panel (.metrics-body), so
 * expanding a section never grows the project page.
 */
export function MetricsBlock({ url, centerlineUrl }: { url: string; centerlineUrl?: string | null }) {
  const [sections, setSections] = useState<MetricsSection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [allOpen, setAllOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = (await (await fetch(url, { mode: 'cors', credentials: 'omit' })).json()) as MeasurementRow[];
        let centerline: [number, number, number][] | null = null;
        if (centerlineUrl) {
          try {
            const line = (await (await fetch(centerlineUrl, { mode: 'cors', credentials: 'omit' })).json()) as { vertices: [number, number, number][] };
            centerline = line.vertices;
          } catch {
            centerline = null;
          }
        }
        if (!cancelled) setSections(computeMetrics(rows, centerline));
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url, centerlineUrl]);

  if (error) return <div className="metrics-note error small">Metrics unavailable: {error}</div>;
  if (!sections) return <div className="metrics-note muted small">Loading metrics…</div>;
  if (!sections.length) return <div className="metrics-note muted small">This run produced no measurements.</div>;
  const isOpen = (t: string) => open[t] ?? allOpen;
  const openCount = sections.filter((s) => isOpen(s.title)).length;
  const toggle = (title: string, section: HTMLElement) => {
    const next = !isOpen(title);
    setOpen({ ...open, [title]: next });
    // Bring the newly opened rows into view inside the scroller; 'nearest' never scrolls further than needed.
    if (next) requestAnimationFrame(() => section.scrollIntoView({ block: 'nearest' }));
  };
  return (
    <div className="metrics">
      <div className="metrics-bar">
        <h3>Metrics</h3>
        <span className="muted">
          {openCount}/{sections.length} open
        </span>
        <button
          className="link small"
          onClick={() => {
            setAllOpen(!allOpen);
            setOpen({});
          }}
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className="metrics-body">
        {sections.map((s) => (
          <div key={s.title} className="metrics-section">
            <button className="metrics-head" aria-expanded={isOpen(s.title)} onClick={(e) => toggle(s.title, e.currentTarget.parentElement!)}>
              <Icon name="chevron-right" className={isOpen(s.title) ? 'chev open' : 'chev'} /> {s.title} <span className="muted">({s.rowCount})</span>
            </button>
            {isOpen(s.title) && (
              <table>
                <tbody>
                  {s.groups.map((g, gi) => (
                    <GroupRows key={gi} title={g.title} rows={g.rows} />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupRows({ title, rows }: { title: string | null; rows: { label: string; value: string }[] }) {
  return (
    <>
      {title && (
        <tr className="group-title">
          <td colSpan={2}>{title}</td>
        </tr>
      )}
      {rows.map((r) => (
        <tr key={r.label} className={title ? 'indent' : ''}>
          <td>{r.label}</td>
          <td>{r.value}</td>
        </tr>
      ))}
    </>
  );
}
